/**
 * ListItem — MD3 list row for the Mango — Farmer Market kit.
 *
 * Per `preview/list-items.html`: a 2-line row (≈72dp when there is supporting
 * text, never below the 56dp rural floor), with a 24px leading icon / avatar,
 * a title, optional supporting text, and an optional trailing element (switch,
 * chevron, status). When `onClick`/`href` is set it renders the pressable
 * `ListItemButton` variant with an MD3 state layer; otherwise a static
 * `ListItem`.
 *
 * Wraps MUI `ListItem` / `ListItemButton` and owns its prop surface (no `sx`
 * leak). `leadingIcon` takes a Material Symbols ligature name; pass `leading`
 * for a custom node (e.g. an avatar).
 */
import {
  ListItem as MuiListItem,
  ListItemButton as MuiListItemButton,
} from '@mui/material';
import { forwardRef, type MouseEventHandler, type ReactNode } from 'react';

import { useColorMode } from '../theme/ColorMode.js';
import { mdSysColor, spacing, tap, typography } from '../theme/tokens.js';
import { Icon } from './Icon.js';

export interface ListItemProps {
  /** Primary line. */
  title: ReactNode;
  /** Optional second line (supporting text). */
  supportingText?: ReactNode;
  /** Leading Material Symbols ligature name (24px). */
  leadingIcon?: string;
  /** Custom leading node (avatar / image) — overrides `leadingIcon`. */
  leading?: ReactNode;
  /** Trailing element (switch, chevron, status pill, value). */
  trailing?: ReactNode;
  /** When set (with onClick/href) the row becomes pressable. */
  onClick?: MouseEventHandler<HTMLElement>;
  /** Render the row as an anchor for navigation. */
  href?: string;
  /** Disable interaction (pressable variant only). */
  disabled?: boolean;
  /** Hide the bottom divider. Default false (divider shown). */
  noDivider?: boolean;
  /** Pass-through className for layout composition. */
  className?: string;
}

export const ListItem = forwardRef<HTMLLIElement, ListItemProps>(function ListItem(
  {
    title,
    supportingText,
    leadingIcon,
    leading,
    trailing,
    onClick,
    href,
    disabled,
    noDivider = false,
    className,
  },
  ref,
) {
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  const pressable = Boolean(onClick || href);

  const leadingNode =
    leading ??
    (leadingIcon ? (
      <Icon name={leadingIcon} size={24} color={sys.onSurfaceVariant} />
    ) : null);

  const rowSx = {
    minHeight: tap.md, // 56px rural floor; grows to ~72 with supporting text
    px: `${spacing.s4}px`,
    py: `${spacing.s3}px`,
    display: 'flex',
    alignItems: 'center',
    gap: `${spacing.s4}px`,
    borderBottom: noDivider ? 'none' : `1px solid ${sys.outlineVariant}`,
  } as const;

  const content = (
    <>
      {leadingNode ? (
        <span
          style={{
            color: sys.onSurfaceVariant,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            flexShrink: 0,
          }}
        >
          {leadingNode}
        </span>
      ) : null}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            fontFamily: typography.fontFamily,
            fontSize: typography.bodyMedium.fontSize,
            fontWeight: 500,
            lineHeight: 1.25,
            color: sys.onSurface,
          }}
        >
          {title}
        </span>
        {supportingText ? (
          <span
            style={{
              display: 'block',
              fontFamily: typography.fontFamily,
              fontSize: typography.bodySmall.fontSize,
              fontWeight: 400,
              lineHeight: 1.4,
              color: sys.onSurfaceVariant,
              marginTop: 2,
            }}
          >
            {supportingText}
          </span>
        ) : null}
      </span>
      {trailing ? (
        <span
          style={{
            color: sys.onSurfaceVariant,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          {trailing}
        </span>
      ) : null}
    </>
  );

  if (pressable) {
    return (
      <MuiListItem ref={ref} className={className} disablePadding sx={{ display: 'block' }}>
        <MuiListItemButton
          component={href ? 'a' : 'div'}
          href={href}
          onClick={onClick}
          disabled={disabled}
          sx={rowSx}
        >
          {content}
        </MuiListItemButton>
      </MuiListItem>
    );
  }

  return (
    <MuiListItem ref={ref} className={className} sx={rowSx}>
      {content}
    </MuiListItem>
  );
});
